import { useMutation, useQuery } from '@tanstack/react-query';
import { FileSignature, Loader2 } from 'lucide-react';
import { fetchSignatures, openSignature } from '../api/candidate';
import dayjs from '../lib/dates';

const KINDS = { quote: 'Devis', order: 'Commande' };

/**
 * Devis et commandes que l'auto-école a envoyés en signature : l'élève connecté
 * les signe d'ici, sans retrouver le lien reçu par e-mail. Un code lui est
 * envoyé, que la page de signature vérifie.
 */
export default function PendingSignatures() {
  const { data } = useQuery({
    queryKey: ['candidate', 'signatures'],
    queryFn: fetchSignatures,
  });
  const open = useMutation({
    mutationFn: openSignature,
    onSuccess: (challenge) => {
      const access = new URLSearchParams({
        challenge: challenge.challengeId,
        email: challenge.email,
        kind: challenge.documentKind,
      });
      window.location.assign(`/signer#${access.toString()}`);
    },
  });

  const documents = data ?? [];
  if (documents.length === 0) return null;

  return (
    <div className="bg-white rounded-2xl border border-indigo-200 p-6 shadow-sm">
      <h2 className="text-slate-900 mb-1">À signer</h2>
      <p className="text-sm text-slate-500 mb-4">
        Votre auto-école vous a envoyé{' '}
        {documents.length > 1 ? `${documents.length} documents` : 'un document'}{' '}
        à signer en ligne. Un code vous sera envoyé par e-mail pour confirmer
        votre identité.
      </p>
      <div className="divide-y divide-slate-100">
        {documents.map((document) => (
          <div
            key={document.requestId}
            className="flex items-center gap-4 py-3.5 first:pt-0"
          >
            <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
              <FileSignature className="text-indigo-600" size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-900 truncate">
                {KINDS[document.kind] ?? 'Document'}{' '}
                {document.documentName.replace(/\.pdf$/i, '')}
              </div>
              <div className="text-xs text-slate-500">
                {document.signed
                  ? 'Votre signature est enregistrée. Une autre signature est attendue.'
                  : `À signer avant le ${dayjs(document.expiresAt).format('DD/MM/YYYY')}`}
              </div>
            </div>
            {!document.signed && (
              <button
                type="button"
                onClick={() => open.mutate(document.requestId)}
                disabled={open.isPending}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-indigo-600 rounded-lg px-3 py-1.5 hover:bg-indigo-700 disabled:opacity-50 flex-shrink-0"
              >
                {open.isPending && open.variables === document.requestId && (
                  <Loader2 size={14} className="animate-spin" />
                )}
                Signer
              </button>
            )}
          </div>
        ))}
      </div>
      {open.isError && (
        <p role="alert" className="text-xs text-red-600 mt-3">
          {open.error instanceof Error && open.error.message
            ? open.error.message
            : 'Envoi du code impossible. Réessayez dans une minute.'}
        </p>
      )}
    </div>
  );
}
